import type {AxiosError} from 'axios';
import axios from 'axios';
import {Form} from '@/plugins/FWForms/index';
import {StorageService} from '@/services/storage.service';
import * as types from '../mutation-types';
import type {VehicleClass, VehicleInterface} from '@/types/vehicle';
import type {TaxCategory} from '@/types/taxes';
import type {TripExtra} from '@/types/trip-extra';
import {
    type ListingFilter,
    type ListingImportInterface,
    type ListingInterface,
    ListingLoadingSubject,
    type ListingPricing,
    type ListingPricingPeriod,
    type ListingPricingPriceType
} from '@/types/listing';
import type {BookingInterface} from '@/types/booking';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import type {RootState} from '@/types/RootState';
import type {Module} from 'vuex'
import {push} from 'notivue'
import {handleApiError} from '@/utils/errorHandler';
import {enqueueListingWrite} from '@/utils/listingWriteQueue';


export type DailyPrice = {
    date: string,
    localizedDayOfWeek: string,
    localizedShortDayOfWeek: string,
    price: number,
    source: 'custom' | 'default',
}

type BookableTime = {
    timeF: string;
    time: string;
};

export type VehicleIncidentPhoto = {
    uuid: string,
    url: string,
    file_name: string | null,
    created_by: string | null,
    created_at: string,
}

export type VehicleIncident = {
    amount: number | null,
    charged_amount: number | null,
    coordinates: { x: number, y: number },
    created_at: string,
    created_by: string,
    damage_type: string,
    fault: string,
    severity: string | null,
    status: string,
    b_id: string | null,
    description: string,
    fixed_at: string,
    incident_at: string,
    updated_at: string,
    uuid: string,
    photos: VehicleIncidentPhoto[],
}

export type VehicleIncidentMeta = {
    damage_types: string[],
    faults: string[],
    severities: string[],
    statuses: string[],
}

export interface VehicleFeature {
    value: string;
    label: string;
}

export type ListingSaveStatus = {
    state: 'idle' | 'saving' | 'saved' | 'error',
    savedAt: string | null,
    message: string | null,
};

export interface ListingState {
    saveStatus: ListingSaveStatus,
    loading: {
        [key in ListingLoadingSubject]: boolean;
    },
    dialogs: Record<string, boolean>,
    snacks: Record<string, boolean>,
    listingInit: boolean,
    allListingsInit: boolean,
    allListings: ListingInterface[],
    custodialListings: ListingInterface[],
    fleetResourceListingsLoaded: boolean,
    fleetResourceListings: ListingInterface[],
    listing: ListingInterface | null,
    originalListing: ListingInterface | null,
    currentListing: ListingInterface,
    listingExtras: any[],
    currentVehicleFeatures: string[],
    allVehicleFeatures: VehicleFeature[],
    dailyPricing: DailyPrice[],
    incidents: VehicleIncident[],
    incidentMeta: VehicleIncidentMeta | null,
    form: any,
    listingBookableTimes: BookableTime[],
    filters: ListingFilter,
    filterOptions: Record<string, any>,
}

const defaultVehicle: VehicleInterface = {
    v_id: '',
    active: false,
    year: null,
    make: '',
    model: '',
    trim: '',
    color: '',
    title: '',
    vin: '',
    features: null,
    in_service: false,
    vehicle_class: null,
    plate: '',
    details: null,
    seats: null,
    doors: null,
    branch: null,
    last_odometer_reading: 0,
    toll_transponder: '',
    has_remote_capability: false,
    lockbox_code: '',
    lockbox_location: '',
    matched_bouncie_vehicle: null,
    matched_getaround_vehicle: undefined,
    status: null,
    unit_id: null,
    matched_turo_vehicle: null,
};


const state: ListingState = {
    loading: Object.values(ListingLoadingSubject).reduce((acc, subject) => {
        acc[subject] = false;
        return acc;
    }, {} as Record<ListingLoadingSubject, boolean>),
    dialogs: {
        pricingOptions: false,
    },
    filters: {
        year: {
            selected: false,
            years: [],
            available_years: [],
        },
        make: {
            selected: false,
            makes: [],
            available_makes: [],
        },
        model: {
            selected: false,
            models: [],
            available_models: [],
        },
        price_type: {
            selected: false,
            price_types: [],
            available_price_types: [],
        },
        branch: {
            selected: false,
            branch_uuids: [],
            available_branches: [],
        },
        vehicle_status: {
            selected: false,
            statuses: [],
            available_statuses: [],
        },
        has_description: {
            selected: false,
            value: null,
        },
        has_images: {
            selected: false,
            value: null,
        },
    },

    saveStatus: {
        state: 'idle',
        savedAt: null,
        message: null,
    },

    snacks: {
        vehicle_details_success: false,
    },
    listingInit: false,
    allListingsInit: false,

    form: new Form(),
    allListings: [],
    custodialListings: [],
    listing: null,

    originalListing: null,
    currentListing: {
        active: false,
        init: false,
        approved: false,
        loaded: false,
        l_id: null,
        unit_id: null,
        name: '',
        description: '',
        slug: '',
        sub_title: '',
        timezone: '',
        checkout_mode: '',
        payment_collection_mode: 'charge_now',
        archived_at: '',
        cover_photo: '',
        listing_image: '',
        order: 0,
        paused: false,
        use_secondary_driver: false,
        use_esign_template: false,
        longer_weekend_trip_preferred: false,
        miles_included: 0,
        miles_extra: 0,
        extras: [],
        distance_allowance_max: 0,
        minimum_request_duration: null,
        maximum_request_duration: null,
        min_age: 0,
        seasoned_driver: 0,
        provider_listings: null,
        tax_category: null,
        is_custodian: false,
        title: '',
        pricing: {
            price_type: 'flat_fee',
            price_period: 'Day',
            price_structure_id: null,
            amount: 1,
            base_price_in_cents: 0,
            security_deposit_credit: 0,
            security_deposit_debit: 0,
            adjusted_amount: 0,
            discountable: false,
            taxable: false,
            original_price: 0,
            price_structure: null,
            sec_dep: 0,
            total_per_day: 0,
            monthlyPercentageDiscount: 0,
            threeDayPercentageDiscount: 0,
            weeklyPercentageDiscount: 0,
        },
        listing_images: [],
        padding_before: 0,
        padding_after: 0,

        vehicle: {
            v_id: '',
            active: false,
            year: null,
            make: '',
            model: '',
            trim: '',
            color: '',
            title: '',
            vin: '',
            features: null,
            in_service: false,
            vehicle_class: null,
            plate: '',
            details: null,
            seats: null,
            doors: null,
            branch: null,
            last_odometer_reading: 0,
            toll_transponder: '',
            has_remote_capability: false,
            lockbox_code: '',
            lockbox_location: '',
            matched_bouncie_vehicle: null,
            matched_getaround_vehicle: undefined,
            status: null,
            unit_id: null,
            matched_turo_vehicle: null,
        },
        bookings: [],
        age_requirements: {
            min_age: 21,
            young_driver: 21,
            young_driver_fee: 19,
            young_driver_security_deposit: 250,
            seasoned_driver: 25,
        },
        commission_partners: [],
        template: null,
        create_mode: 'manual',
    },
    dailyPricing: [],
    allVehicleFeatures: [],
    currentVehicleFeatures: [],
    fleetResourceListingsLoaded: false,
    fleetResourceListings: [],
    incidents: [],
    incidentMeta: null,
    listingExtras: [],
    listingBookableTimes: [],
    filterOptions: {
        sort: 'standard',
        make: [],
    }
};

const getters = {
    allListings: (state: ListingState) => state.allListings,
    filters: (state: ListingState) => state.filters,
    fleetResourceListings: (state: ListingState) => state.fleetResourceListings,
    listingInit: (state: ListingState) => state.listingInit,
    allListingsInit: (state: ListingState) => state.allListingsInit,
    dailyPricing: (state: ListingState) => state.dailyPricing,
    originalListing: (state: ListingState) => state.originalListing,
    currentListing: (state: ListingState) => state.currentListing,
    listingSaveStatus: (state: ListingState) => state.saveStatus,
    currentListingBookings: (state: ListingState) => state.currentListing.bookings,
    listingLoading: (state: ListingState) => state.loading,
    listingSnacks: (state: ListingState) => state.snacks,
    loading: (state: ListingState) => state.loading,
    listing: (state: ListingState) => state.listing,
    form: (state: ListingState) => state.form,
    currentVehicleFeatures: (state: ListingState) => state.currentVehicleFeatures,
    allVehicleFeatures: (state: ListingState) => state.allVehicleFeatures,
    listingExtras: (state: ListingState) => state.listingExtras,
    dialogs: (state: ListingState) => state.dialogs,
    incidents: (state: ListingState) => state.incidents,
    incidentMeta: (state: ListingState) => state.incidentMeta,
    filterOptions: (state: ListingState) => state.filterOptions,

    visibleListings: (state: ListingState) => {
        return (state.allListings || [])
            .filter(listing => !listing.paused && listing.active && listing.approved)
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    },
};

function setFilterValues(state: ListingState, filterKey: string, dataKey: string, data: string[] | number[]) {
    if (data) {
        state.filters[filterKey][dataKey] = data;
    }
}

const mutations = {
    [types.SET_LISTING_LOADING](state: ListingState, {subject, loading}: {
        subject: ListingLoadingSubject;
        loading: boolean
    }) {
        state.loading[subject] = loading;
    },
    [types.SET_LISTING_SNACK](state: ListingState, snack: { subject: string, value: boolean }) {
        state.snacks[snack.subject] = snack?.value || false;
    },
    [types.SET_LISTING_DIALOG](state: ListingState, payload: { topic: string, value: boolean }) {
        state.dialogs[payload.topic] = payload.value;

        setTimeout(() => {
            state.dialogs[payload.topic] = false;
        }, 500);
    },
    [types.SET_LISTING_FILTER_AVAILABLE_FILTERS](state: ListingState, data: {
        years?: number[],
        makes?: string[],
        models?: string[],
        price_types?: string[],
        branches?: Array<{ uuid: string, name: string }>,
        vehicle_statuses?: string[],
    }) {
        setFilterValues(state, 'year', 'available_years', data.years ?? []);
        setFilterValues(state, 'make', 'available_makes', data.makes ?? []);
        setFilterValues(state, 'model', 'available_models', data.models ?? []);
        if (data.price_types !== undefined) {
            state.filters.price_type.available_price_types = data.price_types;
        }
        if (data.branches !== undefined) {
            state.filters.branch.available_branches = data.branches;
        }
        if (data.vehicle_statuses !== undefined) {
            state.filters.vehicle_status.available_statuses = data.vehicle_statuses;
        }
    },
    [types.SET_LISTING_FILTER_SELECTED_FILTERS](state: ListingState, data: {
        years?: number[],
        makes?: string[],
        models?: string[],
        price_types?: string[],
        branch_uuids?: string[],
        vehicle_statuses?: string[],
        has_description?: boolean | null,
        has_images?: boolean | null,
    }) {
        if (data.years !== undefined) setFilterValues(state, 'year', 'years', data.years);
        if (data.makes !== undefined) setFilterValues(state, 'make', 'makes', data.makes);
        if (data.models !== undefined) setFilterValues(state, 'model', 'models', data.models);
        if (data.price_types !== undefined) {
            state.filters.price_type.price_types = data.price_types;
        }
        if (data.branch_uuids !== undefined) {
            state.filters.branch.branch_uuids = data.branch_uuids;
        }
        if (data.vehicle_statuses !== undefined) {
            state.filters.vehicle_status.statuses = data.vehicle_statuses;
        }
        if (data.has_description !== undefined) {
            state.filters.has_description.value = data.has_description;
        }
        if (data.has_images !== undefined) {
            state.filters.has_images.value = data.has_images;
        }
    },
    [types.TOGGLE_LISTING_FILTER](state: ListingState, filter: 'year' | 'make' | 'model' | 'price_type' | 'branch' | 'vehicle_status' | 'has_description' | 'has_images') {
        state.filters[filter].selected = !state.filters[filter].selected;
    },
    [types.CLEAR_LISTING_FILTER](state: ListingState) {
        state.filters.year.years = [];
        state.filters.make.makes = [];
        state.filters.model.models = [];
        state.filters.price_type.price_types = [];
        state.filters.branch.branch_uuids = [];
        state.filters.vehicle_status.statuses = [];
        state.filters.has_description.value = null;
        state.filters.has_images.value = null;
        state.filters.year.selected = false;
        state.filters.make.selected = false;
        state.filters.model.selected = false;
        state.filters.price_type.selected = false;
        state.filters.branch.selected = false;
        state.filters.vehicle_status.selected = false;
        state.filters.has_description.selected = false;
        state.filters.has_images.selected = false;
    },
    [types.SET_LISTING](state: ListingState, listing: ListingInterface) {
        state.listing = listing;
        state.listingInit = true;
    },
    [types.SET_LISTING_INIT](state: ListingState, isInit: boolean) {
        state.listingInit = isInit;
    },
    [types.SET_ALL_LISTINGS](state: ListingState, listings: ListingInterface[]) {
        state.allListings = listings;
        state.allListingsInit = true;
    },
    [types.SET_BOOKABLE_TIMES](state: ListingState, times: BookableTime[]) {
        state.listingBookableTimes = times;
    },
    [types.SET_CUSTODIAL_LISTING](state: ListingState, listings: ListingInterface[]) {
        state.custodialListings = listings;
    },
    [types.SET_FLEET_RESOURCE_LISTINGS](state: ListingState, listings: ListingInterface[]) {
        state.fleetResourceListingsLoaded = true;
        state.fleetResourceListings = listings;
    },
    [types.SET_FLEET_RESOURCE_LISTINGS_DATA](state: ListingState, {l_id, data}) {
        const listing = state.fleetResourceListings.find(item => item.l_id === l_id);

        if (listing) {
            // Iterate over the keys in the data
            Object.keys(data).forEach(key => {
                if (typeof data[key] === 'object' && data[key] !== null) {
                    // Ensure the nested object exists
                    if (!listing[key]) listing[key] = {};

                    // Iterate over the nested object's keys
                    Object.keys(data[key]).forEach(subKey => {
                        listing[key][subKey] = data[key][subKey];
                    });
                } else {
                    // Direct assignment for non-object properties
                    listing[key] = data[key];
                }
            });
        }
    },

    [types.SET_VEHICLE_VIN](state: ListingState, vin: string) {
        if (state.currentListing?.vehicle) {
            state.currentListing.vehicle.vin = vin;
        }
    },
    [types.SET_CURRENT_LISTING_INCIDENTS](state: ListingState, incidents: VehicleIncident[]) {
        state.incidents = incidents;
    },
    [types.SET_VEHICLE_INCIDENT_META](state: ListingState, meta: VehicleIncidentMeta) {
        state.incidentMeta = meta;
    },
    [types.SET_LISTING_FILTER_OPTIONS](state: ListingState, filterOptions: { key: string, value: any }) {
        console.dir('SET_LISTING_FILTER_OPTIONS');
        console.dir(filterOptions);
        state.filterOptions[filterOptions.key] = filterOptions.value;
    },
    [types.SET_VEHICLE_YEAR](state: ListingState, value: number) {
        // Ensure the 'vehicle' object exists in 'currentListing'
        if (!state.currentListing.vehicle) {
            state.currentListing.vehicle = {...defaultVehicle};
        }

        // Directly set the 'year' property on the 'vehicle' object
        state.currentListing.vehicle.year = value;
    },
    [types.SET_LISTING_PRICE_TYPE](state: ListingState, value: ListingPricingPriceType) {
        if (!state.currentListing.pricing) {
            state.currentListing.pricing = {} as ListingPricing;
        }
        state.currentListing.pricing.price_type = value;
    },
    [types.SET_LISTING_PRICE_PERIOD](state: ListingState, value: ListingPricingPeriod) {
        if (!state.currentListing.pricing) {
            state.currentListing.pricing = {} as ListingPricing;
        }
        state.currentListing.pricing.price_period = value;
    },
    [types.SET_LISTING_PRICE_STRUCTURE_ID](state: ListingState, value: string) {
        if (!state.currentListing.pricing) {
            state.currentListing.pricing = {} as ListingPricing;
        }
        state.currentListing.pricing.price_structure_id = value;
    },
    [types.SET_ALL_AVAILABLE_FEATURES](state: ListingState, features: VehicleFeature[]) {
        state.allVehicleFeatures = features;
    },
    [types.SET_VEHICLE_EXTRAS](state: ListingState, extras: TripExtra[]) {
        state.listingExtras = extras;
    },
    [types.SET_VEHICLE_FEATURES](state: ListingState, features: VehicleFeature[]) {
        state.currentVehicleFeatures = features.map((feature) => feature.value);
    },
    [types.SET_VEHICLE_MAKE](state: ListingState, value: string) {
        if (!state.currentListing.vehicle) {
            state.currentListing.vehicle = {...defaultVehicle};
        }
        state.currentListing.vehicle.make = value;
    },
    [types.SET_VEHICLE_MODEL](state: ListingState, value: string) {
        if (!state.currentListing.vehicle) {
            state.currentListing.vehicle = {...defaultVehicle};
        }
        state.currentListing.vehicle.model = value;
    },
    [types.SET_VEHICLE_COLOR](state: ListingState, color: string) {
        if (!state.currentListing.vehicle) {
            state.currentListing.vehicle = {...defaultVehicle};
        }
        state.currentListing.vehicle.color = color;
    },
    [types.SET_VEHICLE_SEATS](state: ListingState, seats: number) {
        if (!state.currentListing.vehicle) {
            state.currentListing.vehicle = {...defaultVehicle};
        }
        state.currentListing.vehicle.seats = seats;
    },
    [types.SET_VEHICLE_DOORS](state: ListingState, doors: number) {
        if (!state.currentListing.vehicle) {
            state.currentListing.vehicle = {...defaultVehicle};
        }
        state.currentListing.vehicle.doors = doors;
    },
    [types.SET_VEHICLE_PLATE](state: ListingState, plate: string) {
        if (!state.currentListing.vehicle) {
            state.currentListing.vehicle = {...defaultVehicle};
        }
        state.currentListing.vehicle.plate = plate;
    },
    [types.SET_VEHICLE_CLASS](state: ListingState, value: VehicleClass) {
        state.currentListing = {
            ...state.currentListing,
            vehicle: {
                ...(state.currentListing.vehicle as VehicleInterface),
                vehicle_class: value,
            },
        };
    },

    [types.TOGGLE_PAUSE_CURRENT_LISTING](state: ListingState) {
        state.currentListing.paused = !state.currentListing.paused;
    },
    [types.SET_CURRENT_LISTING_CHECKOUT_MODE](state: ListingState, mode: string) {
        state.currentListing.checkout_mode = mode;
    },
    [types.SET_EXISTING_LISTING_DATA](state: ListingState, data: Partial<ListingInterface>) {
        // Create a new currentListing object with the updated data
        state.currentListing = {
            ...state.currentListing,
            ...Object.keys(data).reduce((acc, key) => {
                if (data[key] === null || typeof data[key] !== 'object') {
                    // For null or non-object properties, assign directly
                    acc[key] = data[key];
                } else {
                    // For object properties, create a new object with merged properties
                    acc[key] = {
                        ...(state.currentListing[key] || {}),
                        ...data[key]
                    };
                }
                return acc;
            }, {})
        };
    },
    [types.SET_CURRENT_NEW_LISTING](state: ListingState, listing: ListingInterface) {
        if (!listing.vehicle || !listing.pricing) return;

        state.currentListing = {
            ...state.currentListing,
            name: listing.name,
            item_type: listing.item_type ?? 'vehicle',
            item_label: listing.item_label ?? null,
            vehicle: {
                ...state.currentListing.vehicle as VehicleInterface,
                year: listing.vehicle.year,
                make: listing.vehicle.make,
                model: listing.vehicle.model,
                color: listing.vehicle.color,
                plate: listing.vehicle.plate,
            },
            pricing: {
                ...state.currentListing.pricing as ListingPricing,
                amount: listing.pricing.amount,
                price_period: listing.pricing.price_period,
            }
        };
    },

    [types.SET_CURRENT_LISTING](state: ListingState, listing: ListingInterface) {
        state.currentListing.loaded = true;

        if (listing.vehicle === null) {
            state.currentListing = {...state.currentListing, ...listing};

            // Reset vehicle object
            state.currentListing.vehicle = {...defaultVehicle};
        } else {
            state.currentListing = {...state.currentListing, ...listing};
        }
        // Direct property assignment for age_requirements
        state.currentListing.age_requirements = {
            min_age: listing.min_age || 21,
            seasoned_driver: listing.seasoned_driver || 25
        };

        state.listingInit = true;

        // Deep clone using JSON methods
        state.originalListing = JSON.parse(JSON.stringify(state.currentListing));


        // Find and update or add the listing in allListings
        let allListingIndex = -1;
        // if state.allListings is undefined, set it to an empty array
        if (!state.allListings) {
            state.allListings = [];
        }

        if (state.allListings) {
            allListingIndex = state.allListings.findIndex(l => l.l_id === listing.l_id);
        }
        if (allListingIndex >= 0) {
            // Preserve availability data if the new listing doesn't have it
            const existingListing = state.allListings[allListingIndex];
            const updatedListing = {...listing};
            if (existingListing?.available && !updatedListing.available) {
                updatedListing.available = existingListing.available;
            }
            state.allListings[allListingIndex] = updatedListing;
        } else {
            state.allListings.push(listing);
        }
    },

    [types.SET_CURRENT_LISTING_BOOKINGS](state: ListingState, bookings: BookingInterface[]) {
        state.currentListing.bookings = bookings;
    },
    [types.SET_CURRENT_LISTING_MILE_ALLOWANCE](state: ListingState, data: {
        miles_included?: number;
        distance_allowance_max?: number
    }) {
        if (data.miles_included) {
            state.currentListing.miles_included = data.miles_included;
        }
        if (data.distance_allowance_max) {
            state.currentListing.distance_allowance_max = data.distance_allowance_max;
        }
    },
    [types.SET_CURRENT_LISTING_DISCOUNTABLE](state: ListingState, discountable: boolean) {
        if (state.currentListing?.pricing) {
            state.currentListing.pricing.discountable = discountable;
        }
    },

    [types.SET_CURRENT_LISTING_DURATION_DISCOUNT](state: ListingState, discount: {
        threeDayPercentageDiscount?: number;
        weeklyPercentageDiscount?: number;
        monthlyPercentageDiscount?: number;
    }) {
        const pricing = state.currentListing?.pricing;
        if (!pricing) return;

        if (discount.threeDayPercentageDiscount !== undefined) {
            pricing.threeDayPercentageDiscount = discount.threeDayPercentageDiscount;
        }
        if (discount.weeklyPercentageDiscount !== undefined) {
            pricing.weeklyPercentageDiscount = discount.weeklyPercentageDiscount;
        }
        if (discount.monthlyPercentageDiscount !== undefined) {
            pricing.monthlyPercentageDiscount = discount.monthlyPercentageDiscount;
        }
    },
    [types.SET_LISTING_PRICE](state: ListingState, amount: number) {
        if (state.currentListing?.pricing) {
            state.currentListing.pricing.amount = amount;
        }
    },
    [types.SET_LISTING_VEHICLE](state: ListingState, data: any) {
        if (data.last_odometer_reading && state.currentListing?.vehicle) {
            state.currentListing.vehicle.last_odometer_reading = data.last_odometer_reading;
        }
    },
    [types.SET_LISTING_PRICE_TAXABLE](state: ListingState, taxable: boolean) {
        if (state.currentListing?.pricing) {
            state.currentListing.pricing.taxable = taxable;
        }
    },
    [types.SET_LISTING_PRICE_TAX_CATEGORY](state: ListingState, tax_category: TaxCategory) {
        state.currentListing.tax_category = tax_category;
    },
    [types.SET_LISTING_SEC_DEP](state: ListingState, amount: number) {
        if (state.currentListing?.pricing) {
            state.currentListing.pricing.sec_dep = amount;
        }
    },
    [types.SET_LISTING_SEC_DEP_CREDIT](state: ListingState, amount: number) {
        if (state.currentListing?.pricing) {
            state.currentListing.pricing.security_deposit_credit = amount;
        }
    },
    [types.SET_LISTING_SEC_DEP_DEBIT](state: ListingState, amount: number) {
        if (state.currentListing?.pricing) {
            state.currentListing.pricing.security_deposit_debit = amount;
        }
    },
    [types.SET_LISTING_MILES_OVERAGE](state: ListingState, amount: number) {
        state.currentListing.miles_extra = amount;
    },
    [types.SET_CURRENT_LISTING_IMAGES](state: ListingState, images: any[]) {
        state.currentListing.listing_images = images;
    },
    [types.SET_LISTING_DAILY_PRICES](state: ListingState, prices: DailyPrice[]) {
        state.dailyPricing = prices;
    },
    [types.CLEAR_LISTING_FORM_ERRORS](state: ListingState, form_field: string | null = null) {
        if (form_field === null || form_field === undefined) {
            state.form.errors.clear();
        } else {
            state.form.errors.clear(form_field);
        }
    },
    [types.SET_LISTING_SAVE_STATUS](state: ListingState, status: Partial<ListingSaveStatus>) {
        state.saveStatus = {...state.saveStatus, ...status};
    },
    [types.SET_LISTING_FORM_ERRORS](state: ListingState, errors: string | Record<string, string[]>) {
        state.form.errors.set(errors);
    },
    [types.RESET_CURRENT_LISTING](state: ListingState) {
        state.currentListing = {
            active: false,
            approved: false,
            archived_at: '',
            checkout_mode: '',
            payment_collection_mode: 'charge_now',
            commission_partners: null,
            cover_photo: '',
            distance_allowance_max: 0,
            is_custodian: false,
            l_id: '',
            listing_image: '',
            longer_weekend_trip_preferred: false,
            miles_extra: 0,
            miles_included: 0,
            min_age: 0,
            minimum_request_duration: null,
            maximum_request_duration: null,
            order: 0,
            paused: false,
            provider_listings: null,
            seasoned_driver: 0,
            slug: '',
            sub_title: '',
            timezone: '',
            title: '',
            extras: [],
            use_esign_template: false,
            use_secondary_driver: false,
            loaded: false,
            name: '',
            pricing: {
                price_type: 'flat_fee',
                price_period: 'Day',
                price_structure_id: null,
                amount: 1,
                base_price_in_cents: 0,
                security_deposit_credit: 0,
                security_deposit_debit: 0,
                adjusted_amount: 0,
                discountable: false,
                taxable: false,
                original_price: 0,
                price_structure: null,
                sec_dep: 0,
                total_per_day: 0,
                monthlyPercentageDiscount: 0,
                threeDayPercentageDiscount: 0,
                weeklyPercentageDiscount: 0,
            },
            listing_images: [],
            padding_before: 0,
            padding_after: 0,
            vehicle: {...defaultVehicle},
            create_mode: 'manual',
            bookings: []
        };
        state.originalListing = null;
    },
};

const actions = {

    setAvailableFilters({commit}, data: {
        years?: number[],
        makes?: string[],
        models?: string[],
        price_types?: string[],
        branches?: Array<{ uuid: string, name: string }>,
        vehicle_statuses?: string[],
    }) {
        commit(types.SET_LISTING_FILTER_AVAILABLE_FILTERS, data);
    },
    toggleListingFilter({commit}, filter: 'year' | 'make' | 'model' | 'price_type' | 'branch' | 'vehicle_status' | 'has_description' | 'has_images') {
        commit(types.TOGGLE_LISTING_FILTER, filter);
    },
    setSelectedFilters({commit}, data: {
        years?: number[],
        makes?: string[],
        models?: string[],
        price_types?: string[],
        branch_uuids?: string[],
        vehicle_statuses?: string[],
        has_description?: boolean | null,
        has_images?: boolean | null,
    }) {
        commit(types.SET_LISTING_FILTER_SELECTED_FILTERS, data);
    },

    clearFilters({commit}) {
        commit(types.CLEAR_LISTING_FILTER);
    },

    /**
     * Fetch a listing
     */
    async fetchListing({commit}, l_id: string) {
        try {
            const responseData = await performApiRequest(commit, 'SET_LISTING_LOADING', 'listing', () => axios.get(`/v1/listing/${l_id}`));
            commit(types.SET_LISTING, responseData.listing);
            commit(types.SET_CURRENT_LISTING, responseData.listing);
            commit(types.SET_VEHICLE_FEATURES, responseData?.listing?.vehicle?.details);
            commit('booking/SET_BOOKING_LISTING', responseData.listing, {root: true});

            commit('SET_LISTING_INIT', true);
            return responseData.listing;
        } catch (error) {
            handleApiError(error);
        }
    },

    /**
     * Fetch all listings
     */
    async fetchListings({commit}) {
        try {
            const responseData = await performApiRequest(commit, 'SET_LISTING_LOADING', 'listings', () => axios.get('/v1/listings'));
            commit(types.SET_ALL_LISTINGS, responseData.listings);
            commit(types.SET_LISTING_INIT, true);
        } catch (error) {
            handleApiError(error);
        }
    },

    /**
     * Fetch all fleet resource listings
     */
    async fetchFleetResourcesListings({commit}, refresh = false) {
        if (state.fleetResourceListingsLoaded && !refresh) {
            return;
        }

        try {
            const responseData = await performApiRequest(commit, 'SET_LISTING_LOADING', 'listings', () => axios.get('/v1/fleet-resource-listings'));
            commit('SET_FLEET_RESOURCE_LISTINGS', responseData.listings);
        } catch (error) {
            handleApiError(error);
        }
    },


    /**
     * Add a listing
     */
    async addListing({commit}, listing: ListingInterface) {
        try {
            commit(types.CLEAR_LISTING_FORM_ERRORS);

            const responseData = await performApiRequest(commit, 'SET_LISTING_LOADING', 'addListing', () => axios.post('/v1/listings', listing));
            commit(types.SET_CURRENT_LISTING, responseData.listing);
            push.success({
                message: 'New listing added successfully!',
            });

            return responseData.listing;
        } catch (error) {
            if (!axios.isAxiosError(error)) {
                throw error;
            }

            const errors = (error.response?.data as { errors?: Record<string, string[]> })?.errors;
            const message = (error.response?.data as { message?: string })?.message || 'There was an error';
            const statusCode = error.response?.status;

            commit(types.SET_LISTING_FORM_ERRORS, errors);

            // Expected client errors: toast + form errors only — do not reject (VUE-9JW).
            if (statusCode === 401 || statusCode === 422 || statusCode === 403) {
                push.error({message});
                return;
            }

            throw error;
        }
    },


    /**
     * Delete a listing
     */
    async deleteListing({commit}, listing: ListingInterface) {
        try {
            await performApiRequest(commit, 'SET_LISTING_LOADING', 'deleteListing', () => axios.delete(`/v1/listings/${listing.l_id}`));
            push.success({
                message: 'Listing has been removed',
            });

            return true;
        } catch (error) {
            handleApiError(error);

            return false;
        }
    },


    /**
     * Update the order of the listings
     */
    async updateOrder({commit}, listings: { l_id: string | null; order: number; }[]) {
        try {
            const responseData = await performApiRequest(commit, 'SET_LISTING_LOADING', 'listings_order', () => axios.put('/v1/listings/order', listings));
            commit('SET_ALL_LISTINGS', responseData.listings);
            commit('SET_FLEET_RESOURCE_LISTINGS', responseData.listings);
        } catch (error) {
            handleApiError(error);
        }
    },


    /**
     * Get a listing booking
     */
    fetchListingBookings({commit}, l_id: string) {
        axios.get(`/v1/listings/${l_id}/bookings?future=1`)
            .then((response) => {
                commit(types.SET_CURRENT_LISTING_BOOKINGS, response.data.bookings);
            }).catch(() => {
        }).finally(() => {
            commit('SET_LISTING_INIT', true);
        });
    },

    /**
     * Get a listing booking
     */
    async getVehicleIncidents({commit}, payload: string | { l_id?: string, b_id?: string, status?: string }) {
        try {
            const params = typeof payload === 'string' ? {l_id: payload} : payload;
            const query = new URLSearchParams(
                Object.entries(params).filter(([, v]) => !!v) as [string, string][]
            ).toString();
            const responseData = await performApiRequest(commit, 'SET_LISTING_LOADING', 'getIncidents', () => axios.get(`/v1/vehicle-incidents?${query}`));
            commit(types.SET_CURRENT_LISTING_INCIDENTS, responseData.incidents);
            if (responseData.meta) {
                commit(types.SET_VEHICLE_INCIDENT_META, responseData.meta);
            }
        } catch (error) {
            handleApiError(error);
        }
    },

    /**
     * Save a vehicle incident
     */
    async saveIncident({commit}, data: Partial<VehicleIncident> & { l_id?: string }) {
        try {
            const responseData = await performApiRequest(commit, 'SET_LISTING_LOADING', 'saveIncident', () => axios.post('/v1/vehicle-incidents', data));
            if (responseData.meta) {
                commit(types.SET_VEHICLE_INCIDENT_META, responseData.meta);
            }
            return responseData.vehicleIncident;
        } catch (error) {
            handleApiError(error);
        }
    },
    /**
     * Update a vehicle incident
     */
    async updateIncident({commit}, data: VehicleIncident) {
        try {
            await performApiRequest(commit, 'SET_LISTING_LOADING', 'updateIncident', () => axios.put(`/v1/vehicle-incidents/${data.uuid}`, data));
        } catch (error) {
            handleApiError(error);
        }
    },

    /**
     * Delete a vehicle incident
     */
    async deleteIncident({commit}, uuid: string) {
        try {
            await performApiRequest(commit, 'SET_LISTING_LOADING', 'deleteIncident', () => axios.delete(`/v1/vehicle-incidents/${uuid}`));
        } catch (error) {
            handleApiError(error);
        }
    },

    /**
     * Upload photos for a vehicle incident
     */
    async uploadIncidentPhotos({commit, state}, {uuid, files}: { uuid: string, files: File[] }) {
        try {
            const formData = new FormData();
            files.forEach((file) => formData.append('files[]', file));
            const responseData = await performApiRequest(commit, 'SET_LISTING_LOADING', 'uploadIncidentPhotos', () => axios.post(`/v1/vehicle-incidents/${uuid}/photos`, formData, {
                headers: {'Content-Type': 'multipart/form-data'},
            }));
            commit(types.SET_CURRENT_LISTING_INCIDENTS, state.incidents.map(
                (incident: VehicleIncident) => incident.uuid === uuid ? responseData.vehicleIncident : incident
            ));
        } catch (error) {
            handleApiError(error);
        }
    },

    /**
     * Delete a photo from a vehicle incident
     */
    async deleteIncidentPhoto({commit, state}, {uuid, photoUuid}: { uuid: string, photoUuid: string }) {
        try {
            const responseData = await performApiRequest(commit, 'SET_LISTING_LOADING', 'deleteIncidentPhoto', () => axios.delete(`/v1/vehicle-incidents/${uuid}/photos/${photoUuid}`));
            commit(types.SET_CURRENT_LISTING_INCIDENTS, state.incidents.map(
                (incident: VehicleIncident) => incident.uuid === uuid ? responseData.vehicleIncident : incident
            ));
        } catch (error) {
            handleApiError(error);
        }
    },


    /**
     * Set an unavailable period
     */
    async setUnavailablePeriod({commit}, period: {
        l_id: string,
        trip_start: string,
        trip_end: string,
        source: object,
        notes: string,
        supplemental: boolean
    }) {
        try {
            commit('CLEAR_LISTING_FORM_ERRORS');
            const responseData = await performApiRequest(commit, 'SET_LISTING_LOADING', 'unavailable_period', () => axios.post(`/v1/listings/${period.l_id}/unavailability`, period));
            push.success({
                message: 'Unavailable period set successfully!',
            });
            commit(types.SET_CURRENT_LISTING_BOOKINGS, responseData.bookings);
            commit('SET_LISTING_INIT', true);

            return responseData;
        } catch (error) {
            const err = error as AxiosError;
            const errors = (err?.response?.data as Record<string, string[]>)?.errors;
            commit('SET_LISTING_FORM_ERRORS', errors);
            handleApiError(error, 'There was an error setting the unavailable period');
        }
    },


    /**
     * Remove an unavailable period
     */
    removeUnavailablePeriod({commit}, period: { l_id: string, b_id: string }) {
        commit('SET_LISTING_LOADING', {subject: 'unavailable_period', loading: true});

        axios.delete(`/v1/listings/${period.l_id}/unavailability/${period.b_id}`)
            .then((response) => {
                commit(types.SET_CURRENT_LISTING_BOOKINGS, response?.data?.bookings ?? []);
                push.success({
                    message: 'Unavailable period removed successfully!',
                });
            }).catch((error) => {
            handleApiError(error, 'There was an error removing this unavailable period');
        }).finally(() => {
            commit('SET_LISTING_LOADING', {subject: 'unavailable_period', loading: false});
            commit('SET_LISTING_INIT', true);
        });
    },


    /**
     * Update an unavailable period
     */
    async updateUnavailablePeriod({commit}, period: {
        l_id: string,
        b_id: string,
        trip_start: string,
        trip_end: string
    }) {
        try {
            const responseData = await performApiRequest(commit, 'SET_LISTING_LOADING', 'unavailable_period', () => axios.put(`/v1/listings/${period.l_id}/unavailability/${period.b_id}`, period));
            commit(types.SET_CURRENT_LISTING_BOOKINGS, responseData.bookings);
            push.success({
                message: 'Unavailable period updated successfully!',
            });
            commit('SET_LISTING_INIT', true);

            return responseData;
        } catch (error) {
            handleApiError(error);

            return undefined;
        }
    },


    /**
     * Get a company listing
     */
    async fetchCompanySingleListings({commit}, l_id: string) {
        commit('SET_LISTING_LOADING', {subject: 'listing', loading: true});

        await axios.get(`/v1/listings/${l_id}`)
            .then((response) => {
                commit('SET_CURRENT_LISTING', response.data.listing);
                commit('SET_VEHICLE_FEATURES', response?.data?.listing?.vehicle?.details ?? {});
                commit('SET_VEHICLE_EXTRAS', response?.data?.listing?.extras ?? []);
            }).catch(() => {
            }).finally(() => {
                commit('SET_LISTING_LOADING', {subject: 'listing', loading: false});
                commit('SET_LISTING_INIT', true);
            });
    },

    fetchCompanySingleListingExtras({commit}, l_id: string) {
        commit('SET_LISTING_LOADING', {subject: 'listing_extras', loading: true});

        axios.get(`/v1/listings/${l_id}/extras`)
            .then((response) => {
                commit('SET_VEHICLE_EXTRAS', response?.data?.extras ?? []);
            }).catch(() => {
        }).finally(() => {
            commit('SET_LISTING_LOADING', {subject: 'listing_extras', loading: false});
        });
    },

    /**
     * Update a vehicles detail
     */
    async updateVehicleDetails({commit, dispatch}, data: { v_id: string, l_id: string, vehicleFeatures: string[] }) {
        try {
            await performApiRequest(commit, 'SET_LISTING_LOADING', 'vehicleDetails', () => axios.put(`/v1/vehicles/${data.v_id}/details`, data));
            dispatch('fetchCompanySingleListings', data.l_id);
            commit('SET_LISTING_SNACK', {subject: 'vehicle_details_success', value: true});
            return true;
        } catch (error) {
            handleApiError(error);
            return false;
        }
    },


    /**
     * Reset the current listing
     */
    resetListing({commit}) {
        commit(types.RESET_CURRENT_LISTING);
    },


    /**
     * Save a listing
     */
    async saveListing({commit, state}, listing?: Partial<ListingInterface>) {
        // Resolve the listing id up front, but build the body inside the queued
        // task. A field save (PATCH) may still be in flight when the toolbar's
        // save is dispatched; by the time this runs it has landed and committed
        // SET_CURRENT_LISTING, so reading state here picks up the fresh value
        // instead of the snapshot the caller held at click time.
        const listingId = listing?.l_id ?? state.currentListing?.l_id;

        if (!listingId) {
            return;
        }

        try {
            const responseData = await performApiRequest(
                commit,
                'SET_LISTING_LOADING',
                'saveListing',
                () => enqueueListingWrite(
                    listingId,
                    () => axios.put(`/v1/listings/${listingId}`, listing ?? state.currentListing),
                ),
            );
            commit('CLEAR_LISTING_FORM_ERRORS');
            push.success({
                message: 'Listings saved successfully',
            });
            commit('SET_CURRENT_LISTING', responseData.listing);
        } catch (error) {
            const err = error as AxiosError;
            const errors = (err?.response?.data as Record<string, string[]>)?.errors;
            commit('SET_LISTING_FORM_ERRORS', errors);
            handleApiError(error, 'There was an error saving the listing');
        }
    },


    /**
     * Pause a listing
     */
    async pauseListing({commit, state}, data: { mode: string, listings: string[] }) {
        try {
            await performApiRequest(commit, 'SET_LISTING_LOADING', 'pausing', () => axios.put('/v1/listings/pause', {
                mode: data.mode,
                listings: data.listings
            }));
            push.success({
                message: 'Listings saved successfully',
            });

            // Loop through data.listings and update the paused state
            data.listings.forEach((listing) => {
                const index = state.fleetResourceListings.findIndex((item: ListingInterface) => item.l_id === listing);

                if (index !== -1) {
                    commit('SET_FLEET_RESOURCE_LISTINGS_DATA', {
                        l_id: listing,
                        data: {
                            paused: !state.fleetResourceListings[index].paused,
                        },
                    });
                }
            });
            return true;
        } catch (error) {
            handleApiError(error, 'There was an error pausing the listing');

            return false;
        }
    },


    /**
     * Fetch a Listings daily pricing
     */
    async fetchDailyPrices({commit}, {l_id, start, end}) {
        try {
            let qString = '';
            if (start) {
                qString += `?start=${start}`;
            }
            if (end) {
                qString += start ? `&end=${end}` : `?end=${end}`;
            }

            const responseData = await performApiRequest(commit, 'SET_LISTING_LOADING', 'dailyPrices', () => axios.get(`/v1/listings/${l_id}/daily-pricing${qString}`));
            commit(types.SET_LISTING_DAILY_PRICES, responseData.dailyPricing);
        } catch (error) {
            handleApiError(error);
        }
    },


    /**
     * Update a Listings daily pricing
     */
    async updateDailyPrice({commit}, data: { l_id: string, date: string, price: number, sources: object }) {
        return await performApiRequest(commit, 'SET_LISTING_LOADING', 'updateDailyPrices', () => axios.put(`/v1/listings/${data.l_id}/daily-pricing`, data));
    },

    /**
     * Remove a custom daily price
     */
    async removeDailyPrice({commit}, data: { l_id: string, date: string }) {
        return await performApiRequest(commit, 'SET_LISTING_LOADING', 'removeDailyPrice', () => axios.delete(`/v1/listings/${data.l_id}/daily-pricing`, {data}));
    },

    /**
     * Remove multiple custom daily prices
     */
    async removeManyDailyPrices({commit}, data: { l_id: string, dates: string[] }) {
        return await performApiRequest(commit, 'SET_LISTING_LOADING', 'removeManyDailyPrices', () => axios.delete(`/v1/listings/${data.l_id}/daily-pricing/many`, {data}));
    },

    /**
     * Remove all custom daily pricing for a listing
     */
    async removeAllCustomPricing({commit}, data: { l_id: string }) {
        return await performApiRequest(commit, 'SET_LISTING_LOADING', 'removeAllCustomPricing', () => axios.delete(`/v1/listings/${data.l_id}/daily-pricing/all`));
    },

    /**
     * Match Turo vehicle
     */
    async matchTuroVehicle({commit}, data: {
        listing_id: string,
        turo_vehicle_id: string,
        turo_account_uuid: string
    }): Promise<boolean> {
        commit('SET_LISTING_LOADING', {subject: 'saveListing', loading: true});

        try {
            try {
                const response = await axios.post(`/v1/listings/${data.listing_id}/match-turo-vehicle`, {
                    id: data.turo_vehicle_id,
                    turo_account_uuid: data.turo_account_uuid
                });
                commit('SET_ALL_LISTINGS', response?.data?.listings ?? []);
                commit('SET_CURRENT_LISTING', response?.data?.listing ?? []);
                push.success({
                    message: 'Matched Turo vehicle has been added from this listing.',
                });
                return true;
            } catch (error) {
                handleApiError(error);
                return false;
            }
        } finally {
            commit('SET_LISTING_LOADING', {subject: 'saveListing', loading: false});
        }
    },

    /**
     * Match Bouncie vehicles
     */
    async matchBouncieVehicle({commit}, {l_id, bouncie_uuid}): Promise<boolean> {
        commit('SET_LISTING_LOADING', {subject: 'matchBouncieVehicle', loading: true});

        try {
            const response = await axios.post(`/v1/listings/${l_id}/match-bouncie-vehicle`, {id: bouncie_uuid});
            commit('SET_ALL_LISTINGS', response?.data?.listings ?? []);
            commit('SET_CURRENT_LISTING', response?.data?.listing ?? []);
            push.success({
                message: 'Matched Bouncie vehicle has been added from this listing.',
            });

            return true;
        } catch {
            return false;
        } finally {
            commit('SET_LISTING_LOADING', {subject: 'matchBouncieVehicle', loading: false});
        }
    },


    /**
     * Un-match Bouncie vehicles
     */
    async unMatchBouncieVehicle({commit}, l_id: string): Promise<boolean> {
        commit('SET_LISTING_LOADING', {subject: 'saveListing', loading: true});

        try {
            const response = await axios.delete(`/v1/listings/${l_id}/matched-bouncie-vehicle`);
            commit('SET_ALL_LISTINGS', response?.data?.listings ?? []);
            commit('SET_CURRENT_LISTING', response?.data?.listing ?? []);
            push.success({
                message: 'Matched Bouncie vehicle has been removed from this listing.',
            });

            return true;
        } catch {
            return false;
        } finally {
            commit('SET_LISTING_LOADING', {subject: 'saveListing', loading: false});
        }
    },

    async matchTeslaVehicle({commit}, data: { l_id: string, tesla: string }): Promise<boolean> {
        commit('SET_LISTING_LOADING', {subject: 'matchingTeslaVehicle', loading: true});

        try {
            const response = await axios.post(`/v1/listings/${data.l_id}/match-tesla-vehicle`, {uuid: data.tesla});
            commit('SET_ALL_LISTINGS', response?.data?.listings ?? []);
            commit('SET_CURRENT_LISTING', response?.data?.listing ?? []);
            commit('tesla/SET_TESLA_CONNECTIONS', response?.data?.company?.connections?.tesla ?? [], {root: true});
            commit('tesla/SET_CURRENT_WORKING_TESLA_VEHICLE', response?.data?.teslaVehicle ?? [], {root: true});
            push.success({
                message: 'Matched Tesla vehicle has been added from this listing.',
            });

            return true;
        } catch {
            return false;
        } finally {
            commit('SET_LISTING_LOADING', {subject: 'matchingTeslaVehicle', loading: false});
        }
    },

    async matchGetaroundVehicle({commit}, vehicle: { id: string }): Promise<boolean> {
        commit('SET_LISTING_LOADING', {subject: 'saveListing', loading: true});
        try {
            try {
                const response = await axios.post(`/v1/company/${StorageService.getCompanyUuid()}/listings/${state.currentListing.l_id}/match-getaround-vehicle`, {id: vehicle.id});
                commit('SET_ALL_LISTINGS', response.data.listings);
                commit('SET_CURRENT_LISTING', response.data.listing);
                push.success({
                    message: 'Matched Getaround vehicle has been added from this listing.',
                });
                return true;
            } catch {
                return false;
            }
        } finally {
            commit('SET_LISTING_LOADING', {subject: 'saveListing', loading: false});
        }
    },


    /**
     * Un-match a Turo vehicle from Fleetwire
     */
    unMatchTuroVehicle({commit, state}) {
        commit('SET_LISTING_LOADING', {subject: 'saveListing', loading: true});
        axios.delete(`/v1/listings/${state.currentListing.l_id}/matched-turo-vehicle`)
            .then((response) => {
                commit('SET_ALL_LISTINGS', response.data.listings);
                commit('SET_CURRENT_LISTING', response.data.listing);
                push.success({
                    message: 'Matched Turo vehicle has been removed from this listing.',
                });
            }).catch(() => {
        }).finally(() => {
            commit('SET_LISTING_LOADING', {subject: 'saveListing', loading: false});
        });
    },

    unMatchTeslaVehicle({commit}, listing: ListingInterface) {
        commit('SET_LISTING_LOADING', {subject: 'matchingTeslaVehicle', loading: true});
        axios.delete(`/v1/company/${StorageService.getCompanyUuid()}/listings/${listing.l_id}/matched-tesla-vehicle`)
            .then((response) => {
                commit('SET_ALL_LISTINGS', response.data.listings);
                commit('SET_CURRENT_LISTING', response.data.listing);
                commit('tesla/SET_CURRENT_WORKING_TESLA_VEHICLE', response.data?.teslaVehicle, {root: true});
                push.success({
                    message: 'Matched Tesla vehicle has been removed from this listing.',
                });
            }).catch(() => {
        }).finally(() => {
            commit('SET_LISTING_LOADING', {subject: 'matchingTeslaVehicle', loading: false});
        });
    },

    unMatchGetaroundVehicle({commit}) {
        commit('SET_LISTING_LOADING', {subject: 'saveListing', loading: true});
        axios.delete(`/v1/company/${StorageService.getCompanyUuid()}/listings/${state.currentListing.l_id}/matched-getaround-vehicle`)
            .then((response) => {
                commit('SET_CURRENT_LISTING', response.data.listing);
                push.success({
                    message: 'Matched Getaround vehicle has been removed from this listing.',
                });
            }).catch(() => {
        }).finally(() => {
            commit('SET_LISTING_LOADING', {subject: 'saveListing', loading: false});
        });
    },


    /**
     * Archive a listing
     */
    async archiveListing({commit}, listing: ListingInterface) {
        try {
            await performApiRequest(commit, 'SET_LISTING_LOADING', 'archiveListing', () => axios.patch(`/v1/listings/${listing.l_id}/archive`));
            push.success({message: 'Listing has been archived'});
        } catch (error) {
            handleApiError(error);
        }
    },

    async unarchiveListing({commit}, listing: ListingInterface) {
        try {
            await performApiRequest(commit, 'SET_LISTING_LOADING', 'archiveListing', () => axios.patch(`/v1/listings/${listing.l_id}/unarchive`));
            push.success({message: 'Listing has been restored'});
        } catch (error) {
            handleApiError(error);
        }
    },


    /**
     * Delete a listing
     */
    async deleteListings({commit}, listings: string[]) {
        try {
            await performApiRequest(commit, 'SET_LISTING_LOADING', 'deleteListing', () => axios.post('/v1/listings/delete', {listings}));
            push.success({
                message: 'Listing has been removed',
            });

            return true;
        } catch (error) {
            handleApiError(error);

            return false;
        }
    },

    updateListingImageOrder({commit}, images: string[]) {
        commit('SET_LISTING_LOADING', {subject: 'saveListing', loading: true});

        axios.put(`/v1/listings/${state.currentListing.l_id}/images`, {images})
            .then(() => {
            }).catch(() => {
        }).finally(() => {
            commit('SET_LISTING_LOADING', {subject: 'saveListing', loading: false});
        });
    },

    /**
     *
     */
    async getVehicleFeatures({commit, state}) {
        if (state.allVehicleFeatures.length > 0) {
            return;
        }

        try {
            const responseData = await performApiRequest(commit, 'SET_LISTING_LOADING', 'vehicleFeatures', () => axios.get('/v1/vehicle-features'));
            commit(types.SET_ALL_AVAILABLE_FEATURES, responseData.features);
        } catch (error) {
            handleApiError(error);
        }
    },


    /**
     * Delete a single listing image
     */
    async deleteListingImage({state, commit}, image: string) {
        commit('SET_LISTING_LOADING', {subject: 'importDelete', loading: true});

        try {
            try {
                const response = await axios.delete(`/v1/listings/${state.currentListing.l_id}/images/${image}`);
                push.success({
                    message: 'Listing image removed',
                });
                commit(types.SET_CURRENT_LISTING, response.data.listing);
                return true;
            } catch {
                return false;
            }
        } finally {
            commit('SET_LISTING_LOADING', {subject: 'importDelete', loading: false});
        }
    },

    async rotateListingImage({state, commit}, {imageId, degrees = 90}: { imageId: string, degrees?: number }) {
        commit('SET_LISTING_LOADING', {subject: 'importDelete', loading: true});

        try {
            try {
                const response = await axios.post(`/v1/listings/${state.currentListing.l_id}/images/${imageId}/rotate`, {degrees});
                push.success({
                    message: 'Image rotated successfully',
                });
                commit(types.SET_CURRENT_LISTING, response.data.listing);
                return true;
            } catch {
                return false;
            }
        } finally {
            commit('SET_LISTING_LOADING', {subject: 'importDelete', loading: false});
        }
    },

    setListingBookableTimes({commit}) {
        const startTimes: BookableTime[] = [];
        let time: string;
        let timeF: string;
        let ampm: string;
        let lefZero: string | number;

        for (let i = 0; i < 24; i++) {
            time = `${i}:00`;
            timeF = i > 12 ? `${(i - 12)}:00` : `${i}:00`;
            ampm = i >= 12 ? 'pm' : 'am';
            lefZero = i >= 10 ? '' : 0;
            startTimes.push({timeF: `${timeF} ${ampm}`, time: `${lefZero}${time}`});

            time = `${i}:30`;
            timeF = i >= 12 ? `${(i - 12)}:30` : `${i}:30`;
            ampm = i >= 12 ? 'pm' : 'am';
            lefZero = i >= 10 ? '' : 0;
            startTimes.push({timeF: `${timeF} ${ampm}`, time: `${lefZero}${time}`});
        }

        commit('SET_BOOKABLE_TIMES', startTimes);
    },

    async vinDecode({commit}, vin: string) {
        if (vin.length === 17) {
            try {
                const responseData = await performApiRequest(commit, 'SET_LISTING_LOADING', 'decoding_vin', () => axios.get(`/v1/decode-vin/${vin}`));
                return responseData.vehicleData;
            } catch (error) {
                handleApiError(error);
            }
        }
    },


    /**
     * Import listings from csv
     */
    async importCSVListings({commit}, listings: ListingImportInterface[]) {
        commit('CLEAR_LISTING_FORM_ERRORS');

        const responseData = await performApiRequest(commit, 'SET_LISTING_LOADING', 'import_listings', () => axios.post('/v1/listings/import/csv', {listings}));

        commit('SET_ALL_LISTINGS', responseData.listings);
        commit('SET_FLEET_RESOURCE_LISTINGS', responseData.listings);
        push.success({
            message: 'Imported listings successfully',
        });
    },

    /**
     * Update odometer with a provider.
     */
    async updateOdomWithProvider({commit}, {listing, provider}) {
        commit('SET_LISTING_LOADING', {subject: 'updateOdomProvider', loading: true});

        try {
            try {
                await axios.post(`/v1/listings/${listing.l_id}/provider-update-odometer`, {provider});
                return true;
            } catch {
                return false;
            }
        } finally {
            commit('SET_LISTING_LOADING', {subject: 'updateOdomProvider', loading: false});
        }
    },
};

const listingModule: Module<ListingState, RootState> = {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};

export default listingModule;
